import canonical from "../config/canonical";

export const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;

export const HOTJAR_ID = process.env.NEXT_PUBLIC_HOTJAR_ID;

export const ENABLE_VWO = process.env.NEXT_PUBLIC_ENABLE_VWO;

export const OPTIMIZE_ID = process.env.NEXT_PUBLIC_OPTIMIZE_ID;

export const ENABLE_WIQHIT = process.env.NEXT_PUBLIC_ENABLE_WIQHIT;

export const ALGOLIA_APP_ID = process.env.NEXT_PUBLIC_ALGOLIA_APP_ID;

export const ALGOLIA_API_KEY = process.env.NEXT_PUBLIC_ALGOLIA_API_KEY;

export const ENABLE_NEWRELIC = process.env.NEXT_PUBLIC_ENABLE_NEWRELIC;

export const ENABLE_TRACEDOCK = process.env.NEXT_PUBLIC_ENABLE_TRACEDOCK;

export const CANONICAL_DOMAIN = process.env.NEXT_PUBLIC_CANONICAL_DOMAIN;

/**
 * The canonical origin, scheme included. Prefer this over interpolating
 * `CANONICAL_DOMAIN` yourself: it carries a fallback, so it cannot produce
 * `https://undefined/...` when the env var is unset. See config/canonical.js.
 */
export const CANONICAL_HOST = canonical.CANONICAL_HOST;

export const KLAVIYO_COMPANY_ID = process.env.NEXT_PUBLIC_KLAVIYO_COMPANY_ID;

export const STRIPE_PUBLISHABLE_KEY =
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY;

export const BOOST_AD_SERVICE_KEY =
  process.env.NEXT_PUBLIC_BOOST_AD_SERVICE_KEY;

export const NEXT_PUBLIC_GOOGLE_MAPS_PLACE_ID =
  process.env.NEXT_PUBLIC_GOOGLE_MAPS_PLACE_ID;

/**
 * According to official Node.js documentation at https://nodejs.org/api/process.html#process_process_env
 * - Assigning a property on process.env will implicitly convert the value to a string. This behavior is deprecated.
 */
export const GOOGLE_CLIENT_ID =
  process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID?.trim?.() ?? "";

export const FACEBOOK_APP_ID =
  process.env.NEXT_PUBLIC_FACEBOOK_APP_ID?.trim?.() ?? "";

export const DASHBOARD_ENDPOINT =
  process.env.NEXT_PUBLIC_DASHBOARD_ENDPOINT ||
  "https://dashboard.production.whoppah.com";

export const GRAPHQL_ENDPOINT =
  process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT ||
  "https://gateway.production.whoppah.com";

export const SEARCH_ENDPOINT =
  process.env.NEXT_PUBLIC_SEARCH_ENDPOINT || "http://localhost:4040";

export const GRAPHQL_SUBSCRIPTIONS_ENDPOINT =
  process.env.NEXT_PUBLIC_GRAPHQL_SUBSCRIPTIONS_ENDPOINT ||
  "ws://gateway.production.whoppah.com";

export const NEXT_PUBLIC_GOOGLE_MAPS_API_KEY =
  process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY?.trim?.() ?? "";

/**
 * SendCloud *public* API key used by the hosted Service Point Picker widget in
 * checkout. Public by design (it ships to the browser); without it the picker
 * cannot open and the choose-pickup-point button logs an error instead.
 */
export const NEXT_PUBLIC_SENDCLOUD_SPP_KEY =
  process.env.NEXT_PUBLIC_SENDCLOUD_SPP_KEY?.trim?.() ?? "";

export const APPLE_CLIENT_ID = process.env.NEXT_PUBLIC_APPLE_CLIENT_ID || "";
export const APPLE_RETURN_URL = process.env.NEXT_PUBLIC_APPLE_RETURN_URL || "";

export const STATSIG_CLIENT_KEY = process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY;
/**
 * Server-only. Deliberately NOT `NEXT_PUBLIC_` — this is a Statsig *secret*
 * key and must never reach the browser. Only import this from server-side
 * code (getServerSideProps / API routes).
 */
export const STATSIG_SERVER_KEY = process.env.STATSIG_SERVER_KEY;
export const STATSIG_ENVIRONMENT =
  process.env.NEXT_PUBLIC_STATSIG_ENVIRONMENT || "development";

export const STRAPI_ENDPOINT =
  process.env.NEXT_PUBLIC_STRAPI_ENDPOINT || "https://strapi.whoppah.com";

/**
 * Sold product page (WHO-4840, GEO-1).
 *
 * Off by default so the change can ship dark. When off, a gone product
 * keeps its current behaviour: a 301 to a listing page. When on, a sold
 * or gone listing ages out in three phases, resolved in
 * `pages/products/[slug].js`:
 *
 *   Soft-Sold      0 to 90 days    200  sold page + alternatives
 *   Recently Gone  90 to 365 days  410  same page, Gone status
 *   Long Gone      365+ days       301  redirect to a listing page
 *
 * Deliberately server-only, with no `NEXT_PUBLIC_` prefix. The whole
 * decision is taken in `getServerSideProps`, so the variable is read per
 * request and flipping it takes effect immediately. A `NEXT_PUBLIC_`
 * variable would be inlined at build time and would need a redeploy to
 * turn the feature off, which is the opposite of a kill switch.
 *
 * Compared with `=== "true"` rather than tested for truthiness, because
 * the string "false" is truthy and several older flags in this file have
 * that bug.
 */
export const SOLD_PAGE_ENABLED = process.env.SOLD_PAGE_ENABLED === "true";

export const MEGA_MENU_SLUG = process.env.MEGA_MENU_SLUG || "mega-menu";
export const MEGA_MENU_MOBILE_SLUG =
  process.env.MEGA_MENU_MOBILE_SLUG || "mobile";

export const ITERABLE_AUTH_TOKEN =
  process.env.NEXT_PUBLIC_ITERABLE_AUTH_TOKEN || "";

/**
 * WHO-5688. Web mirror of the gateway's global `pro_buyer_program` flag. The
 * gateway only exposes that flag to logged-in business sellers (as a null
 * `tradeDiscount`), so anonymous visitors of /pro-buyer, the menu and the
 * footer have no runtime signal. Set to "true" on Vercel when the program
 * launches; unset means the page 404s and the links are hidden.
 */
export const PRO_BUYER_PROGRAM_ENABLED =
  process.env.NEXT_PUBLIC_PRO_BUYER_PROGRAM === "true";

/**
 * WHO-5947. Web switch for Pro Buyer reservations (48h hold without chat).
 * Unset means every reservation surface renders nothing and sends no request.
 * The gateway additionally returns null/[] while its `pro_buyer_reservations`
 * admin flag is off, and the web renders nothing then too.
 */
export const PRO_BUYER_RESERVATIONS_ENABLED =
  process.env.NEXT_PUBLIC_PRO_BUYER_RESERVATIONS === "true";

/**
 * Homepage "New this week" from the gateway's curated new-in feed
 * (`searchProducts(input: { curatedNewIn: true })`, gateway branch
 * feat/reco-v2-curated-new-in) instead of the whoppah-search newest list.
 * Unset means today's behaviour. Read in `getStaticProps`; a `NEXT_PUBLIC_`
 * value is inlined at build, so flipping it needs a redeploy. Turn it on in
 * Vercel only once the gateway field is live on production: an older gateway
 * rejects the unknown input field, and the page then falls back to search.
 */
export const CURATED_NEW_IN_ENABLED =
  process.env.NEXT_PUBLIC_CURATED_NEW_IN === "true";
